import { useState } from 'react';

import { DiscountStatus } from '@ekhodealer/ekho-common';
import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	MoreButton,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { DiscountsTableRow } from '@ekhodealer/ekho-common';

import Bin24 from '@common/components/icons/Bin24';
import CheckCircle24 from '@common/components/icons/CheckCircle24';
import CircleSlash24 from '@common/components/icons/CircleSlash24';
import Duplicate24 from '@common/components/icons/Duplicate24';
import Eye24 from '@common/components/icons/Eye24';
import SquareUp24 from '@common/components/icons/SquareUp24';

import { useDeleteDiscount } from '../api/useDeleteDiscount';
import { useExportDiscounts } from '../api/useExportDiscounts';
import { useDiscountNavigation } from '../hooks/useDiscountNavigation';
import { useDuplicateDiscount } from '../hooks/useDuplicateDiscount';

import { ActivateDiscountDialog } from './ActivateDiscountDialog';
import { DeactivateDiscountDialog } from './DeactivateDiscountDialog';
import { DeleteDiscountDialog } from './DeleteDiscountDialog';

export interface DiscountActionMenuProps {
	item: DiscountsTableRow;
}

export const DiscountActionMenu = ({ item }: DiscountActionMenuProps) => {
	const isExpired = item.status.value === DiscountStatus.EXPIRED;
	const { openDiscountDetails } = useDiscountNavigation();
	const exportDiscountsMutation = useExportDiscounts();
	const duplicateDiscount = useDuplicateDiscount();
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const deleteDiscountMutation = useDeleteDiscount();

	const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
	const [isActivateDialogOpen, setIsActivateDialogOpen] = useState(false);

	// Action handlers
	const handleView = () => {
		openDiscountDetails(item.id);
	};

	const handleDuplicate = () => {
		duplicateDiscount(item);
	};

	const handleExport = async () => {
		try {
			await exportDiscountsMutation.mutateAsync({
				discountIds: [item.id],
			});

			toast({
				message: '1 discount exported',
				variant: 'success',
			});
		} catch {
			toast({
				message: 'Failed to export discount. Please try again.',
				variant: 'error',
			});
		}
	};

	const handleActivate = () => {
		setIsActivateDialogOpen(true);
	};

	const handleDeactivate = () => {
		setIsDeactivateDialogOpen(true);
	};

	const handleDelete = () => {
		setIsDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = () => {
		deleteDiscountMutation.mutate(item.id, {
			onSuccess: () => {
				toast({ message: 'Discount deleted successfully', variant: 'success' });
				setIsDeleteDialogOpen(false);
			},
			onError: () => {
				toast({ message: 'Failed to delete discount', variant: 'error' });
			},
		});
	};

	return (
		<>
			<ActionMenuTrigger>
				<MoreButton aria-label={`More actions for discount ${item.header.name}`} />
				<ActionMenu>
					<ActionMenuItem
						label='View'
						leadingAccessory={<Eye24 />}
						onAction={handleView}
					/>
					<ActionMenuItem
						label='Duplicate'
						leadingAccessory={<Duplicate24 />}
						onAction={handleDuplicate}
					/>
					<ActionMenuItem
						label='Export'
						leadingAccessory={<SquareUp24 />}
						onAction={handleExport}
					/>
					{isExpired ? (
						<ActionMenuItem
							label='Activate'
							leadingAccessory={<CheckCircle24 />}
							onAction={handleActivate}
						/>
					) : (
						<ActionMenuItem
							label='Deactivate'
							leadingAccessory={<CircleSlash24 />}
							onAction={handleDeactivate}
						/>
					)}
					<ActionMenuItem
						label={
							<Text size='bodySmall' color='error'>
								Delete
							</Text>
						}
						leadingAccessory={<Bin24 />}
						onAction={handleDelete}
					/>
				</ActionMenu>
			</ActionMenuTrigger>
			<DeleteDiscountDialog
				discountName={item.header.name}
				isOpen={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				onConfirm={handleDeleteConfirm}
				isDeleting={deleteDiscountMutation.isPending}
			/>

			<DeactivateDiscountDialog
				discountId={item.id}
				discountName={item.header.name}
				startsAt={item.status.startsAt}
				isOpen={isDeactivateDialogOpen}
				onOpenChange={setIsDeactivateDialogOpen}
			/>

			<ActivateDiscountDialog
				discountId={item.id}
				discountName={item.header.name}
				isOpen={isActivateDialogOpen}
				onOpenChange={setIsActivateDialogOpen}
			/>
		</>
	);
};
